import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bookmark,
  CalendarClock,
  ChevronDown,
  ExternalLink,
  FileText,
  MoreHorizontal,
  PlayCircle,
  Sparkles,
  Trash2,
} from "lucide-react";

import { GrantApplicationDraftEditor } from "@/components/grant_suite/GrantApplicationDraftEditor";
import {
  type GrantArtifactType,
  GRANT_ARTIFACT_TYPE_LABELS,
} from "@/api/grant_artifacts";
import {
  type GrantOpportunity,
  type GrantOpportunityPayload,
  type GrantOpportunityStatus,
  GRANT_OPPORTUNITY_STATUS_LABELS,
} from "@/api/grant_opportunities";
import { listGrantOpportunityComments } from "@/api/grant_opportunity_comments";
import { listGrantOpportunityDocuments } from "@/api/grant_opportunity_documents";
import { listGrantOpportunityEvents } from "@/api/grant_opportunity_events";
import { listGrantOpportunityLinkedDocumentComments } from "@/api/grant_opportunity_linked_document_comments";
import {
  buildGrantOpportunityActivity,
  GrantOpportunityActivity,
} from "@/components/grant_suite/GrantOpportunityActivity";
import { ExternalOrganizationsCard } from "@/components/grant_suite/ExternalOrganizationsCard";
import { FunderPortalCard } from "@/components/grant_suite/FunderPortalCard";
import { GrantOpportunityDocuments } from "@/components/grant_suite/GrantOpportunityDocuments";
import { GrantOpportunityTasks } from "@/components/grant_suite/GrantOpportunityTasks";
import { GrantPlanningModal } from "@/components/grant_suite/GrantPlanningModal";
import { OpportunityResearchPane } from "@/components/grant_suite/OpportunityResearchPane";
import { GrantOpportunityTeamFields } from "@/components/grant_suite/GrantOpportunityTeamFields";
import { GrantNotesThread } from "@/components/grant_suite/GrantNotesThread";
import {
  formatGrantDeadline,
  formatGrantCurrency,
  grantDeadlineSummary,
  grantDeadlineTone,
  grantOpportunitySourceLabel,
  grantOpportunityStatusLabel,
  grantOpportunityStatusPillClass,
  GRANT_OPPORTUNITY_PIPELINE_STATUS_ORDER,
  GRANT_OPPORTUNITY_FORWARD_STAGES,
  GRANT_OPPORTUNITY_STATUS_DESCRIPTIONS,
  grantOpportunityPipelineStatus,
} from "@/components/grant_suite/grantSuiteDisplay";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import DatePicker from "@/components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUser } from "@/context/UserProvider";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";
import { grantOpportunityEventsQueryKey } from "@/lib/grantSuiteQueryKeys";
import { cn } from "@/lib/utils";

type GrantOpportunityDetailProps = {
  municipalityId: number;
  fileViewerEnabled: boolean;
  opportunity: GrantOpportunity;
  onUpdate: (patch: Partial<GrantOpportunityPayload>) => void;
  onRequestDelete: () => void;
  updating: boolean;
  // The patch of the last rejected update, so blur-committed inputs can drop
  // text the server refused (ENG-1872).
  rejectedUpdate?: Partial<GrantOpportunityPayload> | null;
};

function parseDeadline(value: string | null): Date | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

const RESEARCH_TAB = "research";

export function GrantOpportunityDetail({
  municipalityId,
  fileViewerEnabled,
  opportunity,
  onUpdate,
  onRequestDelete,
  updating,
  rejectedUpdate,
}: GrantOpportunityDetailProps) {
  const { currentUser } = useUser();
  // Outside collaborators are view-only (ENG-1877): the API already rejects
  // their writes, so every edit affordance below renders as a value instead.
  const outsideUser = userIsOutsideUser(currentUser);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [draftEditorOpen, setDraftEditorOpen] = useState(false);
  const [artifactEditorType, setArtifactEditorType] = useState<GrantArtifactType | null>(null);
  const [planningModalOpen, setPlanningModalOpen] = useState(false);

  const { data: documents = [] } = useQuery({
    queryKey: ["grant_opportunity_documents", municipalityId, opportunity.id],
    queryFn: () => listGrantOpportunityDocuments(municipalityId, opportunity.id),
    enabled: fileViewerEnabled,
  });

  const { data: comments = [] } = useQuery({
    queryKey: ["grant_opportunity_comments", municipalityId, opportunity.id],
    queryFn: () => listGrantOpportunityComments(municipalityId, opportunity.id),
  });

  const { data: events = [] } = useQuery({
    queryKey: grantOpportunityEventsQueryKey(municipalityId, opportunity.id),
    queryFn: () => listGrantOpportunityEvents(municipalityId, opportunity.id),
  });

  const { data: linkedDocumentComments = [] } = useQuery({
    queryKey: ["grant_opportunity_linked_document_comments", municipalityId, opportunity.id],
    queryFn: () => listGrantOpportunityLinkedDocumentComments(municipalityId, opportunity.id),
    enabled: fileViewerEnabled,
  });

  const activityCount = useMemo(
    () =>
      buildGrantOpportunityActivity(
        comments,
        fileViewerEnabled ? documents : [],
        events,
        fileViewerEnabled ? linkedDocumentComments : [],
      ).length,
    [comments, documents, events, linkedDocumentComments, fileViewerEnabled],
  );

  const deadlineTone = grantDeadlineTone(opportunity.deadline_at);
  const onWatchlist = opportunity.watching;
  const pipelineStatus = grantOpportunityPipelineStatus(opportunity.status);
  // Progress reflects only the forward pipeline (preparing → submitted → awarded).
  // -1 means a terminal off-ramp (declined/closed), which gets a distinct treatment.
  const forwardStageIndex = GRANT_OPPORTUNITY_FORWARD_STAGES.indexOf(pipelineStatus);
  const isAwarded = pipelineStatus === "awarded";

  const startPursuing = () => onUpdate({ watching: false, status: "preparing" });
  const moveToWatchlist = () => onUpdate({ watching: true, status: "interested" });

  const handleDraftSaved = () => {
    queryClient.invalidateQueries({
      queryKey: ["grant_opportunity_documents", municipalityId, opportunity.id],
    });
  };

  const canGenerateDrafts =
    fileViewerEnabled &&
    !outsideUser &&
    Boolean(currentUser?.grant_suite || currentUser?.grant_finder_doc_demo);
  // Mirrors the API's opportunity_research_viewer? gate (grant_suite team flag,
  // never outside users). No file_viewer requirement: the assistant answers from
  // the opportunity package and confirmed plan, not the file library.
  const canUseResearchAssistant = !outsideUser && Boolean(currentUser?.grant_suite);
  const canGenerateApplicationDraft = canGenerateDrafts && Boolean(opportunity.grant_id);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col" data-testid="grant-opportunity-detail">
      <div className="shrink-0 border-b bg-background px-4 py-4 sm:px-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {onWatchlist ? (
                <Badge variant="outline" className="gap-1 font-normal">
                  <Bookmark className="h-3 w-3" />
                  Saved
                </Badge>
              ) : (
                <span
                  className={cn(
                    "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium",
                    grantOpportunityStatusPillClass(pipelineStatus),
                  )}
                >
                  {grantOpportunityStatusLabel(pipelineStatus)}
                </span>
              )}
              <Badge variant="secondary" className="font-normal">
                {grantOpportunitySourceLabel(opportunity.source)}
              </Badge>
            </div>
            <h2 className="text-lg font-semibold leading-snug tracking-tight sm:text-xl">
              {opportunity.title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>Updated {formatRelativeTime(opportunity.updated_at)}</span>
              {opportunity.grant_id && <span>Catalog ID {opportunity.grant_id}</span>}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* The package upload endpoint sits behind :file_viewer, so the
                planning entry needs the same gate as the Files tab. */}
            {opportunity.ai_partner && !outsideUser && fileViewerEnabled ? (
              <Button variant="outline" size="sm" onClick={() => setPlanningModalOpen(true)}>
                <Sparkles className="mr-2 h-3.5 w-3.5" />
                AI Partner plan
              </Button>
            ) : null}
            {canGenerateDrafts ? (
              // modal={false}: selecting an item opens the draft/artifact editor
              // Dialog, which owns its own focus/scroll lock. A modal menu leaves
              // document.body at pointer-events:none, which the Dialog then captures
              // and "restores" on close, freezing the page (ENG-1684).
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <FileText className="mr-2 h-3.5 w-3.5" />
                    Generate draft
                    <ChevronDown className="ml-2 h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canGenerateApplicationDraft ? (
                    <>
                      <DropdownMenuItem onClick={() => setDraftEditorOpen(true)}>
                        Application draft
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  {(Object.keys(GRANT_ARTIFACT_TYPE_LABELS) as GrantArtifactType[]).map((type) => (
                    <DropdownMenuItem key={type} onClick={() => setArtifactEditorType(type)}>
                      {GRANT_ARTIFACT_TYPE_LABELS[type]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {opportunity.url && (
              <Button variant="outline" size="sm" asChild>
                <a href={opportunity.url} target="_blank" rel="noreferrer">
                  Program page
                  <ExternalLink className="ml-2 h-3.5 w-3.5" />
                </a>
              </Button>
            )}
            {/* modal={false}: "Remove opportunity" opens the delete AlertDialog
                (via onRequestDelete). Same pointer-events race as the draft menu —
                canceling the delete would otherwise freeze the page (ENG-1684). */}
            {outsideUser ? null : (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Opportunity actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onWatchlist ? (
                  <DropdownMenuItem onClick={startPursuing} disabled={updating}>
                    <PlayCircle className="mr-2 h-4 w-4" />
                    Start pursuing
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={moveToWatchlist} disabled={updating}>
                    <Bookmark className="mr-2 h-4 w-4" />
                    Move back to Saved
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={onRequestDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove opportunity
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </div>

        {deadlineTone === "warning" && opportunity.deadline_at && (
          <Alert variant="destructive" className="mt-4">
            <CalendarClock className="h-4 w-4" />
            <AlertTitle>Deadline attention</AlertTitle>
            <AlertDescription>{grantDeadlineSummary(opportunity.deadline_at)}</AlertDescription>
          </Alert>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="shrink-0 border-b px-4 py-2 sm:px-6">
          <TabsList className="h-9 w-full justify-start sm:w-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {fileViewerEnabled ? (
              <TabsTrigger value="files" className="gap-1.5">
                Files
                {documents.length > 0 && (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px]">
                    {documents.length}
                  </Badge>
                )}
              </TabsTrigger>
            ) : null}
            {canUseResearchAssistant ? (
              <TabsTrigger value={RESEARCH_TAB}>Research</TabsTrigger>
            ) : null}
            <TabsTrigger value="activity" className="gap-1.5">
              Activity
              {activityCount > 0 && (
                <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px]">
                  {activityCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea
          className={cn("min-h-0 flex-1", activeTab === RESEARCH_TAB && "hidden")}
          viewportClassName="min-w-0"
        >
          <TabsContent value="overview" className="m-0 min-w-0 px-4 py-4 sm:px-6">
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {onWatchlist ? "Saved for later" : "Application status"}
                  </CardTitle>
                  <CardDescription>
                    {onWatchlist
                      ? "Evaluate fit before your team commits. Status tracking begins when you start pursuing."
                      : "Track where this grant sits in your application workflow."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {onWatchlist ? (
                    <>
                      <div className="rounded-lg border bg-muted/20 px-3 py-3 text-sm text-muted-foreground">
                        This grant is on your Saved list — not yet in the pursuit pipeline.
                      </div>
                      {!outsideUser && (
                        <Button
                          variant="default"
                          size="sm"
                          className="w-full"
                          disabled={updating}
                          onClick={startPursuing}
                        >
                          <PlayCircle className="mr-2 h-4 w-4" />
                          Start pursuing
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="detail-status">Status</Label>
                        {outsideUser ? (
                          <p id="detail-status" className="text-sm font-medium">
                            {grantOpportunityStatusLabel(pipelineStatus)}
                          </p>
                        ) : (
                          <Select
                            value={pipelineStatus}
                            onValueChange={(value) =>
                              onUpdate({ status: value as GrantOpportunityStatus })
                            }
                            disabled={updating}
                          >
                            <SelectTrigger id="detail-status">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {GRANT_OPPORTUNITY_PIPELINE_STATUS_ORDER.map((status) => (
                                <SelectItem key={status} value={status}>
                                  {GRANT_OPPORTUNITY_STATUS_LABELS[status]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {GRANT_OPPORTUNITY_STATUS_DESCRIPTIONS[pipelineStatus]}
                        </p>
                      </div>

                      <div className="space-y-2" data-testid="grant-progress">
                        <div className="flex items-center justify-between gap-2">
                          <Label>Progress</Label>
                          {forwardStageIndex >= 0 ? (
                            <span className="text-xs text-muted-foreground">
                              {isAwarded
                                ? "Awarded"
                                : `Step ${forwardStageIndex + 1} of ${GRANT_OPPORTUNITY_FORWARD_STAGES.length}`}
                            </span>
                          ) : null}
                        </div>
                        {forwardStageIndex >= 0 ? (
                          <div className="flex gap-1">
                            {GRANT_OPPORTUNITY_FORWARD_STAGES.map((status, index) => (
                              <div
                                key={status}
                                data-testid="grant-progress-segment"
                                className={cn(
                                  "h-1.5 flex-1 rounded-full",
                                  index <= forwardStageIndex
                                    ? isAwarded
                                      ? "bg-emerald-500"
                                      : "bg-primary"
                                    : "bg-muted",
                                )}
                                title={grantOpportunityStatusLabel(status)}
                              />
                            ))}
                          </div>
                        ) : (
                          // Terminal off-ramp (declined/closed): not a point on the
                          // forward line, so show the outcome as a badge, not a bar.
                          <span
                            className={cn(
                              "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium",
                              grantOpportunityStatusPillClass(pipelineStatus),
                            )}
                          >
                            {grantOpportunityStatusLabel(pipelineStatus)}
                          </span>
                        )}
                      </div>

                      {!outsideUser && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-muted-foreground"
                          disabled={updating}
                          onClick={moveToWatchlist}
                        >
                          <Bookmark className="mr-2 h-3.5 w-3.5" />
                          Move back to Saved
                        </Button>
                      )}
                    </>
                  )}

                  <Separator />

                  <GrantOpportunityTeamFields
                    municipalityId={municipalityId}
                    opportunity={opportunity}
                    onUpdate={onUpdate}
                    updating={updating}
                  />

                  <Separator />

                  <div className="space-y-2">
                    <Label>Working deadline</Label>
                    {outsideUser ? (
                      <p className="text-sm font-medium">
                        {formatGrantDeadline(opportunity.deadline_at)}
                      </p>
                    ) : (
                      <DatePicker
                        value={parseDeadline(opportunity.deadline_at)}
                        onChange={(date) =>
                          onUpdate({
                            deadline_at: date ? date.toISOString() : null,
                          })
                        }
                        placeholder="Set a deadline"
                        disabled={updating}
                      />
                    )}
                    <p
                      className={cn(
                        "text-xs",
                        deadlineTone === "warning"
                          ? "text-amber-700 dark:text-amber-400"
                          : "text-muted-foreground",
                      )}
                    >
                      {grantDeadlineSummary(opportunity.deadline_at)}
                    </p>
                    {!outsideUser && opportunity.deadline_at && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground"
                        disabled={updating}
                        onClick={() => onUpdate({ deadline_at: null })}
                      >
                        Clear deadline
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4">
                {/* Notes are internal-team context; the read itself is denied
                    for outside users, so hide the card rather than render a
                    false "No notes yet" (ENG-1877). */}
                {!outsideUser && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Internal notes</CardTitle>
                      <CardDescription>
                        Team context for this grant. Each note is saved with who wrote it and when.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <GrantNotesThread
                        municipalityId={municipalityId}
                        opportunityId={opportunity.id}
                        notable={{ type: "opportunity" }}
                        currentUserId={currentUser?.id ?? null}
                      />
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent className="pt-6">
                    <GrantOpportunityTasks
                      municipalityId={municipalityId}
                      opportunityId={opportunity.id}
                      departmentId={opportunity.department_id}
                      pursuing={!onWatchlist}
                      awarded={opportunity.status === "awarded"}
                      ownerName={opportunity.assigned_to_name}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <DetailRow label="Source" value={grantOpportunitySourceLabel(opportunity.source)} />
                    <DetailRow
                      label="Tracking"
                      value={onWatchlist ? "Saved" : grantOpportunityStatusLabel(pipelineStatus)}
                    />
                    <DetailRow
                      label="Owner"
                      value={opportunity.assigned_to_name ?? "Unassigned"}
                    />
                    <DetailRow
                      label="Requested"
                      value={formatGrantCurrency(opportunity.requested_amount)}
                    />
                    <DetailRow
                      label="Awarded"
                      value={formatGrantCurrency(opportunity.awarded_amount)}
                    />
                    <DetailRow
                      label="Award date"
                      value={
                        opportunity.award_date
                          ? formatGrantDeadline(opportunity.award_date)
                          : "—"
                      }
                    />
                    <DetailRow
                      label="Deadline"
                      value={formatGrantDeadline(opportunity.deadline_at)}
                    />
                    <DetailRow
                      label="Added"
                      value={formatRelativeTime(opportunity.created_at)}
                    />
                    <DetailRow
                      label="Last updated"
                      value={formatRelativeTime(opportunity.updated_at)}
                    />
                    {opportunity.url && (
                      <DetailRow
                        label="Program URL"
                        value={
                          <a
                            href={opportunity.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            Open link
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        }
                      />
                    )}
                  </CardContent>
                </Card>

                <FunderPortalCard
                  opportunity={opportunity}
                  onUpdate={onUpdate}
                  updating={updating}
                  rejectedUpdate={rejectedUpdate}
                />

                <ExternalOrganizationsCard
                  municipalityId={municipalityId}
                  opportunityId={opportunity.id}
                />
              </div>
            </div>
          </TabsContent>

          {fileViewerEnabled ? (
            <TabsContent value="files" className="m-0 min-w-0 px-4 py-4 sm:px-6">
              <GrantOpportunityDocuments
                municipalityId={municipalityId}
                opportunityId={opportunity.id}
                opportunityTitle={opportunity.title}
                fileViewerEnabled={fileViewerEnabled}
                documentFolderId={opportunity.document_folder_id ?? null}
                embedded
              />
            </TabsContent>
          ) : null}

          <TabsContent value="activity" className="m-0 min-w-0 px-4 py-4 sm:px-6">
            <GrantOpportunityActivity
              municipalityId={municipalityId}
              opportunityId={opportunity.id}
              fileViewerEnabled={fileViewerEnabled}
            />
          </TabsContent>
        </ScrollArea>

        {/* Outside the ScrollArea: the pane scrolls its own thread, and a
            percentage height can't chain through Radix's display:table
            viewport wrapper anyway. As a flex sibling it gets the height that
            is actually left, so the composer stays on screen on short
            viewports. */}
        {/* Radix hides an inactive panel with the `hidden` attribute, whose UA
            `display: none` loses to Tailwind's `flex`. Left unguarded the panel
            stays in flow while empty and its `flex-1` eats half the column, so
            every other tab renders into a pane that stops short with dead space
            beneath it. Emit exactly one display utility. */}
        {canUseResearchAssistant ? (
          <TabsContent
            value={RESEARCH_TAB}
            className={cn(
              "m-0 min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4 sm:px-6",
              activeTab === RESEARCH_TAB ? "flex" : "hidden",
            )}
          >
            {/* The floor and the scroll escape only work as a pair. Below `lg`
                SuiteTrackingLayout stacks a max-h-[40%] sidebar above an
                overflow-hidden detail column, so a floor on its own would clip
                the composer with nowhere to scroll, and no floor at all leaves
                the pane too short to use. 240px is deliberately below the
                ~274px the tightest desktop case has to spend: a taller floor
                would exceed the space available there and reintroduce the
                scrolling this ticket exists to remove. */}
            <div className="flex min-h-[240px] flex-1 flex-col">
              <OpportunityResearchPane
                municipalityId={municipalityId}
                opportunityId={opportunity.id}
                opportunityTitle={opportunity.title}
              />
            </div>
          </TabsContent>
        ) : null}
      </Tabs>

      {canGenerateApplicationDraft && opportunity.grant_id ? (
        <GrantApplicationDraftEditor
          open={draftEditorOpen}
          onOpenChange={setDraftEditorOpen}
          municipalityId={municipalityId}
          opportunityId={opportunity.id}
          opportunityTitle={opportunity.title}
          mode="generate"
          grantId={opportunity.grant_id}
          onSaved={handleDraftSaved}
        />
      ) : null}

      <GrantPlanningModal
        open={planningModalOpen}
        onOpenChange={setPlanningModalOpen}
        municipalityId={municipalityId}
        opportunityId={opportunity.id}
        departmentId={opportunity.department_id}
        opportunityTitle={opportunity.title}
      />

      {canGenerateDrafts && artifactEditorType ? (
        <GrantApplicationDraftEditor
          open
          onOpenChange={(open) => {
            if (!open) setArtifactEditorType(null);
          }}
          municipalityId={municipalityId}
          opportunityId={opportunity.id}
          opportunityTitle={opportunity.title}
          mode="generate"
          artifactType={artifactEditorType}
          onSaved={handleDraftSaved}
        />
      ) : null}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 pb-2 last:border-0 last:pb-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
